import React, { useState, useEffect, useMemo } from 'react';
import { Gift, ShoppingCart, Star, Check, ExternalLink, Loader, CreditCard, ChevronDown, ChevronUp, RotateCcw, Search } from 'lucide-react';
import { Modal, Button } from '../../components/UI';
import { useAuthStore } from '../../store/authStore';
import { rewardsApi, xoxodayApi } from '../../services/api';
import { Reward, RewardRedemption, XoxodayRedemption } from '../../types';
import styles from './Rewards.module.css';

const Rewards: React.FC = () => {
  const { user, updateUser } = useAuthStore();
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [redemptions, setRedemptions] = useState<RewardRedemption[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemSuccess, setRedeemSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState<'catalog' | 'history' | 'marketplace'>('catalog');
  const [searchTerm, setSearchTerm] = useState('');

  // Xoxoday state
  const [xoxodayLoading, setXoxodayLoading] = useState(false);
  const [xoxodayBalance, setXoxodayBalance] = useState<number | null>(null);
  const [xoxodayError, setXoxodayError] = useState('');
  const [xoxodayRedemptions, setXoxodayRedemptions] = useState<XoxodayRedemption[]>([]);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());

  // Calculate redeemable balance (points_balance - 500 reserve for giving)
  const redeemableBalance = Math.max(0, (user?.points_balance || 0) - 500);

  useEffect(() => {
    fetchData();
  }, [selectedCategory]);

  useEffect(() => {
    if (activeTab === 'marketplace') {
      fetchXoxodayBalance();
    }
  }, [activeTab]);

  const fetchXoxodayBalance = async () => {
    try {
      setXoxodayLoading(true);
      setXoxodayError('');
      const response = await xoxodayApi.getMyBalance();
      setXoxodayBalance(response.data.balance);
    } catch (error: any) {
      console.error('Failed to fetch Xoxoday balance:', error);
      setXoxodayError(error.response?.data?.error || 'Failed to load balance');
    } finally {
      setXoxodayLoading(false);
    }
  };

  const handleXoxodayRedeem = async (productUrl?: string) => {
    try {
      setXoxodayLoading(true);
      setXoxodayError('');
      const response = await xoxodayApi.initiateSSO();
      if (response.data.redirectUrl) {
        const ssoUrl = response.data.redirectUrl;
        if (productUrl) {
          window.open(ssoUrl, 'xoxoday_redeem');
          setTimeout(() => {
            window.open(productUrl, 'xoxoday_redeem');
          }, 3500);
        } else {
          window.open(ssoUrl, '_blank');
        }
      }
    } catch (error: any) {
      console.error('Failed to initiate Xoxoday SSO:', error);
      setXoxodayError(error.response?.data?.error || 'Failed to connect to Xoxoday');
    } finally {
      setXoxodayLoading(false);
    }
  };

  const handleCatalogRedeem = (reward: Reward) => {
    if (reward.redirect_url) {
      handleXoxodayRedeem(reward.redirect_url);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rewardsRes, categoriesRes, redemptionsRes, xoxodayHistoryRes] = await Promise.all([
        rewardsApi.getAll({
          category: selectedCategory !== 'all' ? selectedCategory : undefined,
          available_only: true,
        }),
        rewardsApi.getCategories(),
        rewardsApi.getMyRedemptions(),
        xoxodayApi.getRedemptionHistory().catch(() => ({ data: { redemptions: [] } })),
      ]);

      setRewards(rewardsRes.data.rewards);
      setCategories(categoriesRes.data);
      setRedemptions(redemptionsRes.data.redemptions);
      setXoxodayRedemptions(xoxodayHistoryRes.data.redemptions || []);
    } catch (error) {
      console.error('Failed to fetch rewards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (!selectedReward) return;

    setRedeeming(true);
    try {
      await rewardsApi.redeem(selectedReward.id);
      if (user) {
        updateUser({
          points_balance: user.points_balance - selectedReward.points_cost,
        });
      }
      setRedeemSuccess(true);
      setTimeout(() => {
        setShowConfirmModal(false);
        setRedeemSuccess(false);
        setSelectedReward(null);
        fetchData();
      }, 2000);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to redeem reward');
    } finally {
      setRedeeming(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getRedemptionTitle = (redemption: XoxodayRedemption): string => {
    if (!redemption.order_data || !Array.isArray(redemption.order_data) || redemption.order_data.length === 0) {
      return 'Xoxoday Marketplace Order';
    }
    const firstName = redemption.order_data[0].product?.name || 'Xoxoday Order';
    if (redemption.order_data.length > 1) {
      return `${firstName} (+${redemption.order_data.length - 1})`;
    }
    return firstName;
  };

  const toggleOrderExpand = (redemptionId: string) => {
    setExpandedOrders(prev => {
      const next = new Set(prev);
      if (next.has(redemptionId)) {
        next.delete(redemptionId);
      } else {
        next.add(redemptionId);
      }
      return next;
    });
  };

  // Compute summary stats for My Redemptions tab
  const redemptionSummary = useMemo(() => {
    const allRedemptions = [...xoxodayRedemptions];
    const totalCount = allRedemptions.length + redemptions.length;
    const xoxodaySpent = allRedemptions.reduce((sum, r) => sum + parseFloat(String(r.points_redeemed || 0)), 0);
    const catalogSpent = redemptions.reduce((sum, r) => sum + (r.points_spent || 0), 0);
    const totalSpent = xoxodaySpent + catalogSpent;
    const totalRefunded = allRedemptions.reduce((sum, r) => sum + parseFloat(String(r.total_refunded || 0)), 0);
    return { totalCount, totalSpent, totalRefunded };
  }, [xoxodayRedemptions, redemptions]);

  // Filtered rewards for catalog
  const filteredRewards = useMemo(() => {
    if (!searchTerm.trim()) return rewards;
    const term = searchTerm.toLowerCase();
    return rewards.filter(r =>
      r.name.toLowerCase().includes(term) ||
      r.description?.toLowerCase().includes(term) ||
      r.category?.toLowerCase().includes(term)
    );
  }, [rewards, searchTerm]);

  const renderStatusBadge = (status: string, isFullyRefunded: boolean, isPartiallyRefunded: boolean) => {
    if (isFullyRefunded) {
      return <span className={`${styles.statusBadge} ${styles.statusRefunded}`}>Fully Refunded</span>;
    }
    if (isPartiallyRefunded) {
      return <span className={`${styles.statusBadge} ${styles.statusPending}`}>Partially Refunded</span>;
    }
    switch (status) {
      case 'completed':
      case 'fulfilled':
        return <span className={`${styles.statusBadge} ${styles.statusCompleted}`}>Completed</span>;
      case 'pending':
        return <span className={`${styles.statusBadge} ${styles.statusPending}`}>Pending</span>;
      default:
        return <span className={`${styles.statusBadge} ${styles.statusDefault}`}>{status}</span>;
    }
  };

  return (
    <div className={styles.container}>
      {/* ── Page Header ── */}
      <div className={styles.pageHeader}>
        <div>
          <h1>Perks &amp; privileges</h1>
          <p>You have <strong style={{ color: 'var(--primary)' }}>{redeemableBalance} waves</strong>. Everything below is within reach.</p>
        </div>
      </div>

      {/* ── Nav Tabs ── */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'catalog' ? styles.active : ''}`}
          onClick={() => setActiveTab('catalog')}
        >
          <Gift size={16} />
          Catalog
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'history' ? styles.active : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <ShoppingCart size={16} />
          My Redemptions
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'marketplace' ? styles.active : ''}`}
          onClick={() => setActiveTab('marketplace')}
        >
          <ExternalLink size={16} />
          Redeem Points
        </button>
      </div>

      {/* ═══ Tab 1: Catalog ═══ */}
      {activeTab === 'catalog' && (
        <>
          <div className={styles.controlsRow}>
            <div className={styles.filters}>
              <button
                className={`${styles.filterBtn} ${selectedCategory === 'all' ? styles.active : ''}`}
                onClick={() => setSelectedCategory('all')}
              >
                All
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  className={`${styles.filterBtn} ${selectedCategory === category ? styles.active : ''}`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
            <div className={styles.searchBox}>
              <Search size={16} />
              <input
                type="text"
                placeholder="Search rewards..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {loading ? (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <span>Loading rewards...</span>
            </div>
          ) : filteredRewards.length > 0 ? (
            <div className={styles.grid}>
              {filteredRewards.map((reward, idx) => (
                <div
                  key={reward.id}
                  className={styles.rewardCard}
                  style={{ animationDelay: `${idx * 0.04}s` }}
                >
                  <div className={styles.rewardImageArea}>
                    {reward.image_url ? (
                      <img src={reward.image_url} alt={reward.name} />
                    ) : (
                      <Gift size={40} />
                    )}
                    {reward.category && (
                      <span className={styles.categoryBadge}>{reward.category}</span>
                    )}
                  </div>
                  <div className={styles.cardBody}>
                    <h3 className={styles.rewardName}>{reward.name}</h3>
                    {reward.description && (
                      <p className={styles.rewardSubtitle}>{reward.description}</p>
                    )}
                    <div className={styles.wavesRange}>
                      <Star size={15} />
                      {reward.points_cost_max && reward.points_cost_max !== reward.points_cost
                        ? `${reward.points_cost} - ${reward.points_cost_max}`
                        : reward.points_cost}
                      <span style={{ fontWeight: 500, fontSize: 12 }}>waves</span>
                    </div>
                  </div>
                  <div className={styles.cardFooter}>
                    <button
                      className={styles.redeemBtn}
                      onClick={() => handleCatalogRedeem(reward)}
                    >
                      <ExternalLink size={14} />
                      Redeem
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.empty}>
              <Gift size={44} />
              <h3>No rewards available</h3>
              <p>Check back later for new rewards!</p>
            </div>
          )}
        </>
      )}

      {/* ═══ Tab 2: My Redemptions ═══ */}
      {activeTab === 'history' && (
        <>
          {/* Summary Bar */}
          <div className={styles.summaryBar}>
            <div className={styles.summaryCard}>
              <div className={`${styles.summaryIconBox} ${styles.tealBg}`}>
                <ShoppingCart size={20} />
              </div>
              <div>
                <div className={styles.summaryValue}>{redemptionSummary.totalCount}</div>
                <div className={styles.summaryLabel}>Total Redemptions</div>
              </div>
            </div>
            <div className={styles.summaryCard}>
              <div className={`${styles.summaryIconBox} ${styles.amberBg}`}>
                <Star size={20} />
              </div>
              <div>
                <div className={styles.summaryValue}>{redemptionSummary.totalSpent}</div>
                <div className={styles.summaryLabel}>Waves Spent</div>
              </div>
            </div>
            <div className={styles.summaryCard}>
              <div className={`${styles.summaryIconBox} ${styles.redBg}`}>
                <RotateCcw size={20} />
              </div>
              <div>
                <div className={styles.summaryValue}>{redemptionSummary.totalRefunded}</div>
                <div className={styles.summaryLabel}>Waves Refunded</div>
              </div>
            </div>
          </div>

          <div className={styles.redemptionsList}>
            {redemptions.length === 0 && xoxodayRedemptions.length === 0 ? (
              <div className={styles.empty}>
                <ShoppingCart size={44} />
                <h3>No redemptions yet</h3>
                <p>Start redeeming rewards to see them here!</p>
              </div>
            ) : (
              <>
                {/* Xoxoday Marketplace Redemptions */}
                {xoxodayRedemptions.length > 0 && (
                  <>
                    <div className={styles.redemptionSection}>
                      <h3 className={styles.sectionTitle}>
                        <CreditCard size={16} />
                        Marketplace Redemptions
                      </h3>
                    </div>
                    {xoxodayRedemptions.map((redemption) => {
                      const isExpanded = expandedOrders.has(redemption.id);
                      const hasOrderItems = redemption.order_data && Array.isArray(redemption.order_data) && redemption.order_data.length > 0;
                      const totalRefunded = parseFloat(String(redemption.total_refunded || 0));
                      const pointsRedeemed = parseFloat(String(redemption.points_redeemed));
                      const isRefunded = totalRefunded > 0 || !!redemption.refund_id;
                      const isFullyRefunded = totalRefunded >= pointsRedeemed;
                      const isPartiallyRefunded = isRefunded && !isFullyRefunded;
                      const refundCount = redemption.refund_count || 0;

                      return (
                        <div
                          key={`xoxoday-${redemption.id}`}
                          className={`${styles.redemptionCard} ${isExpanded ? styles.redemptionCardExpanded : ''}`}
                        >
                          <div
                            className={styles.redemptionInfo}
                            onClick={() => (hasOrderItems || isRefunded) && toggleOrderExpand(redemption.id)}
                            style={{ cursor: (hasOrderItems || isRefunded) ? 'pointer' : 'default' }}
                          >
                            <div className={`${styles.redemptionIcon} ${isRefunded ? styles.refundIcon : styles.xoxodayIcon}`}>
                              {isRefunded ? <RotateCcw size={20} /> : <CreditCard size={20} />}
                            </div>
                            <div className={styles.redemptionDetails}>
                              <h4>{getRedemptionTitle(redemption)}</h4>
                              <div className={styles.redemptionMeta}>
                                <span className={styles.orderId}>#{redemption.xoxoday_order_id}</span>
                                <span className={styles.redemptionDate}>
                                  {formatDate(redemption.order_date || redemption.created_at)}
                                </span>
                              </div>
                            </div>
                            <div className={styles.redemptionStatus}>
                              {renderStatusBadge(redemption.status, isFullyRefunded, isPartiallyRefunded)}
                              <span className={styles.redemptionPoints}>
                                -{pointsRedeemed} waves
                              </span>
                              {isRefunded && (
                                <span className={styles.refundPoints}>
                                  +{totalRefunded} of {pointsRedeemed} refunded
                                  {refundCount > 1 && (
                                    <span className={styles.refundCountBadge}>
                                      ({refundCount} refunds)
                                    </span>
                                  )}
                                </span>
                              )}
                              {(hasOrderItems || isRefunded) && (
                                <span className={styles.expandToggle}>
                                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Expandable order items & refund history */}
                          {isExpanded && (
                            <div className={styles.orderItemsList}>
                              {hasOrderItems && redemption.order_data!.map((item, index) => (
                                <div key={index} className={styles.orderItem}>
                                  <div className={styles.orderItemName}>
                                    <span>{item.product.name}</span>
                                    <span className={styles.orderItemCatBadge}>{item.product.category}</span>
                                  </div>
                                  <div className={styles.orderItemMeta}>
                                    <span>Qty: {item.quantity}</span>
                                    <span className={styles.orderItemPrice}>{item.orderAmount} waves</span>
                                  </div>
                                </div>
                              ))}

                              {/* Refund history */}
                              {isRefunded && (
                                <div className={styles.refundHistory}>
                                  {refundCount > 1 && redemption.refund_details ? (
                                    <>
                                      <div className={styles.refundHistoryTitle}>
                                        <RotateCcw size={14} />
                                        <span>Refund History ({refundCount} refunds, total: {totalRefunded} waves)</span>
                                      </div>
                                      {redemption.refund_details.map((detail, idx) => (
                                        <div key={idx} className={styles.refundHistoryItem}>
                                          <span className={styles.refundItemAmount}>+{detail.amount} waves</span>
                                          {detail.reason && (
                                            <span className={styles.refundItemReason}>{detail.reason}</span>
                                          )}
                                          {detail.date && (
                                            <span className={styles.refundItemDate}>{formatDate(detail.date)}</span>
                                          )}
                                        </div>
                                      ))}
                                    </>
                                  ) : (
                                    (redemption.latest_refund_reason || redemption.refund_reason) ? (
                                      <div className={styles.refundReason}>
                                        <RotateCcw size={14} />
                                        <span>Refund reason: {redemption.latest_refund_reason || redemption.refund_reason}</span>
                                      </div>
                                    ) : null
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}

                {/* Catalog Redemptions */}
                {redemptions.length > 0 && (
                  <>
                    <div className={styles.redemptionSection}>
                      <h3 className={styles.sectionTitle}>
                        <Gift size={16} />
                        Catalog Redemptions
                      </h3>
                    </div>
                    {redemptions.map((redemption) => (
                      <div key={redemption.id} className={styles.redemptionCard}>
                        <div className={styles.redemptionInfo}>
                          <div className={styles.redemptionIcon}>
                            <Gift size={20} />
                          </div>
                          <div className={styles.redemptionDetails}>
                            <h4>{redemption.reward_name}</h4>
                            <div className={styles.redemptionMeta}>
                              <span className={styles.redemptionDate}>
                                {formatDate(redemption.created_at)}
                              </span>
                            </div>
                          </div>
                          <div className={styles.redemptionStatus}>
                            {renderStatusBadge(redemption.status, false, false)}
                            <span className={styles.redemptionPoints}>
                              -{redemption.points_spent} waves
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* ═══ Tab 3: Redeem Points (Xoxoday SSO) ═══ */}
      {activeTab === 'marketplace' && (
        <div className={styles.marketplaceSection}>
          {/* Dark Hero Banner */}
          <div className={styles.mpHero}>
            <div className={styles.mpHeroDecor1} />
            <div className={styles.mpHeroDecor2} />
            <div className={styles.mpHeroContent}>
              <div className={styles.mpHeroIcon}>🎁</div>
              <h2 className={styles.mpHeroTitle}>Xoxoday Rewards Marketplace</h2>
              <p className={styles.mpHeroDesc}>
                Redeem your waves for gift cards, experiences, and more from thousands of brands worldwide!
              </p>
            </div>
          </div>

          {xoxodayError && (
            <div className={styles.errorBanner}>
              {xoxodayError}
            </div>
          )}

          {/* Balance + Features Card */}
          <div className={styles.mpCard}>
            <div className={styles.mpBalanceRow}>
              <div className={styles.mpBalanceBox}>
                <span className={styles.mpBalanceLabel}>Your Redeemable Balance</span>
                <div className={styles.mpBalanceValue}>
                  {xoxodayLoading ? (
                    <Loader size={22} className={styles.spinner} />
                  ) : (
                    <>
                      <span className={styles.mpBalanceNumber}>
                        {xoxodayBalance !== null ? xoxodayBalance : redeemableBalance}
                      </span>
                      <span className={styles.mpBalanceUnit}>waves</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.mpFeatures}>
              <div className={styles.mpFeature}>
                <div className={styles.mpFeatureIcon} style={{ background: '#ECFDF5' }}>🏪</div>
                <div>
                  <div className={styles.mpFeatureTitle}>5000+ Brands</div>
                  <div className={styles.mpFeatureDesc}>Top global brands</div>
                </div>
              </div>
              <div className={styles.mpFeature}>
                <div className={styles.mpFeatureIcon} style={{ background: '#EEF2FF' }}>🎁</div>
                <div>
                  <div className={styles.mpFeatureTitle}>Gift Cards</div>
                  <div className={styles.mpFeatureDesc}>Cards & experiences</div>
                </div>
              </div>
              <div className={styles.mpFeature}>
                <div className={styles.mpFeatureIcon} style={{ background: '#FFF7ED' }}>⚡</div>
                <div>
                  <div className={styles.mpFeatureTitle}>Instant Delivery</div>
                  <div className={styles.mpFeatureDesc}>Delivered instantly</div>
                </div>
              </div>
            </div>

            <button
              className={styles.mpCta}
              onClick={() => handleXoxodayRedeem()}
              disabled={xoxodayLoading || redeemableBalance === 0}
            >
              <ExternalLink size={18} />
              Open Rewards Marketplace
            </button>

            <p className={styles.mpNote}>
              You'll be redirected to the Xoxoday marketplace in a new tab where you can browse and redeem rewards.
            </p>
          </div>
        </div>
      )}

      {/* ═══ Confirm / Success Modal ═══ */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => {
          if (!redeeming) {
            setShowConfirmModal(false);
            setSelectedReward(null);
          }
        }}
        title={redeemSuccess ? 'Success!' : 'Confirm Redemption'}
        size="sm"
      >
        {redeemSuccess ? (
          <div className={styles.successModal}>
            <div className={styles.successIcon}>
              <Check size={28} />
            </div>
            <h3>Reward Redeemed!</h3>
            <p>Your reward has been successfully redeemed. You'll be notified when it's ready.</p>
          </div>
        ) : (
          <div className={styles.confirmModal}>
            <div className={styles.confirmReward}>
              <div className={styles.confirmImage}>
                {selectedReward?.image_url ? (
                  <img src={selectedReward.image_url} alt={selectedReward?.name} />
                ) : (
                  <Gift size={28} />
                )}
              </div>
              <div>
                <h4>{selectedReward?.name}</h4>
                <p>{selectedReward?.description}</p>
              </div>
            </div>
            <div className={styles.confirmDetails}>
              <div className={styles.confirmRow}>
                <span>Cost</span>
                <span>{selectedReward?.points_cost} waves</span>
              </div>
              <div className={styles.confirmRow}>
                <span>Current Balance</span>
                <span>{redeemableBalance} waves</span>
              </div>
              <div className={`${styles.confirmRow} ${styles.afterBalance}`}>
                <span>Balance After</span>
                <span>{redeemableBalance - (selectedReward?.points_cost || 0)} waves</span>
              </div>
            </div>
            <div className={styles.confirmActions}>
              <Button variant="outline" onClick={() => setShowConfirmModal(false)} disabled={redeeming}>
                Cancel
              </Button>
              <Button onClick={handleRedeem} loading={redeeming}>
                Confirm Redemption
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Rewards;
